import {
  BehaviorSubject,
  empty,
  never,
  Observable,
  timer,
  Subject,
} from 'rxjs';
import { map, flatMap, share, switchMap } from 'rxjs/operators';

import {
  MonitorEngine,
  MonitorEngineInterface,
} from '../api/monitor-engine.interface';
import { AbstractEditableDataStream } from '../utility/editable-data-stream';

const TAG = 'TapMonitorEngine';

type MonitoringControllerSubject = BehaviorSubject<
  Partial<MonitoringController.State>
>;

export namespace MonitoringController {
  export interface State {
    /**
     * If due time is a number, start will be delayed by dueTime value
     * If it's a date, monitoring will start on given date
     */
    dueTime: number | Date;
    /**
     * New monitoring state to activate
     */
    state: MonitorEngine.State;
    /**
     * Monitoring period in milliseconds
     */
    period: number;
    /**
     * True to trigger immediate refresh
     */
    refresh: boolean;
  }
}
export class MonitoringController implements MonitorEngineInterface {
  private subject = new BehaviorSubject<Partial<MonitoringController.State>>(
    this.initalState,
  );

  constructor(
    public readonly initalState: MonitoringController.State = INITAL_MONITORING_CONFIG,
  ) {}

  get state() {
    return this.subject.value.state;
  }

  start(options?: MonitorEngine.StartOptions): this {
    this.subject.next({
      ...{
        state: MonitorEngine.State.START,
      },
      ...options,
    });
    return this;
  }

  stop(): this {
    this.subject.next({
      state: MonitorEngine.State.STOP,
    });
    return this;
  }

  pause(): this {
    this.subject.next({
      state: MonitorEngine.State.PAUSE,
    });
    return this;
  }

  setPeriod(period: number) {
    this.subject.next({
      period,
    });
  }

  asSubject(): MonitoringControllerSubject {
    return this.subject;
  }
}

export function createDataStreamMonitor<
  StreamType extends AbstractEditableDataStream<unknown, DataType>,
  DataType,
>(
  stream: StreamType,
  ticker: Observable<MonitoringTickerState>,
  eventSubject: Subject<{
    type: 'error';
    error: Error;
    stream: StreamType;
    timerState: MonitoringTickerState;
  }>,
) {
  return ticker.pipe(
    flatMap(async (tickerState) => {
      // debug(TAG, `Running call at time ${time}`);
      return stream
        .readRaw()
        .then((value) => {
          // TODO event ?
          return value;
        })
        .catch((error) => {
          eventSubject.next({
            type: 'error',
            error,
            stream,
            timerState: tickerState,
          });
        });
    }),
    // filter<DataType | undefined, DataType>((value): value is DataType => {
    //     return value !== undefined;
    // }),
    share(),
  );
}

interface MonitoringTickerState {
  time: number;
  state: MonitoringController.State;
}

const INITAL_MONITORING_CONFIG: MonitoringController.State = {
  dueTime: 0,
  period: 1000,
  refresh: false,
  state: MonitorEngine.State.STOP,
};

export function createMonitoringTicker(
  // call: (time: number) => Promise<DataType>,
  controller: MonitoringControllerSubject | MonitoringController,
): Observable<MonitoringTickerState> {
  const controllerSubject: MonitoringControllerSubject =
    controller instanceof MonitoringController
      ? controller.asSubject()
      : controller;
  let lastState: MonitoringController.State = {
    ...INITAL_MONITORING_CONFIG,
    ...controllerSubject.value,
  };
  return controllerSubject.pipe(
    switchMap((partialState): Observable<MonitoringTickerState> => {
      const newState: MonitoringController.State = {
        ...lastState,
        ...partialState,
      };
      lastState = newState;
      // console.log(TAG, 'Apply new monitoring state: ', newState);
      switch (newState.state) {
        case MonitorEngine.State.STOP:
          return empty() as Observable<any>;
        case MonitorEngine.State.START:
          return timer(newState.dueTime, newState.period).pipe(
            map((time: number) => ({
              time,
              state: newState,
            })),
          );
        case MonitorEngine.State.PAUSE:
          return never();
        default:
          throw new Error(
            `InternalErorr: Not implemented monitoring state: ${newState.state}`,
          );
      }
    }),
  );
}
